<script setup lang="ts">
import eventHub from 'frontend/_globals/event-hub'
import type { ToastAction } from 'frontend/_stores/toast-store'
import { useCurrentUser } from 'frontend/common/composables/useCurrentUser'
import { UsersService } from 'frontend/users/users-service'
import { inject, onBeforeUnmount, onMounted, type Ref } from 'vue'

const { isImpersonatingOtherUser, impersonatedUserNick } = useCurrentUser()

const usersService = new UsersService()
const showErrorToast: ToastAction = inject('$error')
const showSuccessToast: ToastAction = inject('$success')
const footerTeleportNode: Ref<HTMLElement | null> = inject('footerTeleportNode')

const year = dayjs().year()

onMounted(() => {
  eventHub.$emit('footer-teleport-node', footerTeleportNode)
})

onBeforeUnmount(() => {
  eventHub.$emit('footer-teleport-node', null)
})

async function stopImpersonation() {
  try {
    await usersService.dispatchAction('impersonate', null, {
      data: {
        impersonatedUserId: null,
      },
      errorHandler() {
        showErrorToast({ message: 'Cannot stop impersonating!' })
      },
    })
    showSuccessToast({ message: 'Impersonation stopped!' })
  } catch (error) {
    console.error('error happened during impersonating', error, error?.response)
  }
}
</script>

<template lang="pug">
footer.footer.px-3.small(:class="{ 'is-impersonating': isImpersonatingOtherUser }")
  .wrapper.d-flex.align-items-center.justify-content-center(v-if="isImpersonatingOtherUser")
    .text You are currently impersonating {{ impersonatedUserNick }}
    .button-stop-impersonation(@click="stopImpersonation") Stop impersonation
  .d-flex.align-items-center.justify-content-between(v-else)
    .d-flex.align-items-center.justify-content-start.flex-grow-1
      #footer-teleport(ref="footerTeleportNode")
    .copyright.text-nowrap
      span.me-1 FIFA Transport, © {{ year }}
      a(href="https://www.fifa.com/" target="_blank") FIFA
      span.ms-1 All rights reserved
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;

.footer {
  border-top: 1px solid #b8c1cc;
  color: map.get(themeVariables.$colors, outline);

  .erebus-app[data-theme='dark'] & {
    border-color: #3b434d;
  }

  &.is-impersonating {
    background: #ffca85;
    color: #412501;
    height: 38px;
  }
}

.wrapper {
  height: 100%;
  gap: 8px;
  font-size: 14px;
}

.button-stop-impersonation {
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
</style>
